import React, { useCallback, useEffect, useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring } from "motion/react";
import * as stylex from "@stylexjs/stylex";
import { useIsDarkTheme } from "@/lib/theme-store";
import { cn } from "@/lib/utils/index";
interface MagicCardBaseProps {
  children?: React.ReactNode;
  decoration?: React.ReactNode;
  className?: string;
  stylexStyle?: stylex.StyleXStyles;
  gradientSize?: number;
  gradientFrom?: string;
  gradientTo?: string;
}
interface MagicCardGradientProps extends MagicCardBaseProps {
  mode?: "gradient";
  gradientColor?: string;
  gradientOpacity?: number;
  glowFrom?: never;
  glowTo?: never;
  glowAngle?: never;
  glowSize?: never;
  glowBlur?: never;
  glowOpacity?: never;
}
interface MagicCardOrbProps extends MagicCardBaseProps {
  mode: "orb";
  glowFrom?: string;
  glowTo?: string;
  glowAngle?: number;
  glowSize?: number;
  glowBlur?: number;
  glowOpacity?: number;
  gradientColor?: never;
  gradientOpacity?: never;
}
export type MagicCardProps = MagicCardGradientProps | MagicCardOrbProps;
type ResetReason = "enter" | "leave" | "global" | "init";
function isOrbMode(props: MagicCardProps): props is MagicCardOrbProps {
  return props.mode === "orb";
}

/**
 * Shared global event listener manager for MagicCard instances.
 * Instead of each MagicCard adding its own global listeners (which creates
 * N×3 listeners for N cards), we maintain a single set of listeners and
 * a registry of callbacks.
 */
const globalResetCallbacks = new Set<() => void>();
let globalListenersAttached = false;
function attachGlobalListeners() {
  if (globalListenersAttached) return;
  const handleGlobalPointerOut = (e: PointerEvent) => {
    if (!e.relatedTarget) {
      globalResetCallbacks.forEach(cb => cb());
    }
  };
  const handleBlur = () => {
    globalResetCallbacks.forEach(cb => cb());
  };
  const handleVisibility = () => {
    if (document.visibilityState !== "visible") {
      globalResetCallbacks.forEach(cb => cb());
    }
  };
  window.addEventListener("pointerout", handleGlobalPointerOut);
  window.addEventListener("blur", handleBlur);
  document.addEventListener("visibilitychange", handleVisibility);
  globalListenersAttached = true;
}
function useGlobalResetListener(callback: () => void) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    attachGlobalListeners();
    const stableCallback = () => callbackRef.current();
    globalResetCallbacks.add(stableCallback);
    return () => {
      globalResetCallbacks.delete(stableCallback);
    };
  }, []);
}
export function MagicCard(props: MagicCardProps) {
  const {
    children,
    decoration,
    className,
    stylexStyle,
    gradientSize = 200,
    gradientColor = "#262626",
    gradientOpacity = 0.8,
    gradientFrom = "#9E7AFF",
    gradientTo = "#FE8BBB",
    mode = "gradient"
  } = props;
  const stylexProps = stylex.props(stylexStyle);
  const glowFrom = isOrbMode(props) ? props.glowFrom ?? "#ee4f27" : "#ee4f27";
  const glowTo = isOrbMode(props) ? props.glowTo ?? "#6b21ef" : "#6b21ef";
  const glowAngle = isOrbMode(props) ? props.glowAngle ?? 90 : 90;
  const glowSize = isOrbMode(props) ? props.glowSize ?? 420 : 420;
  const glowBlur = isOrbMode(props) ? props.glowBlur ?? 60 : 60;
  const glowOpacity = isOrbMode(props) ? props.glowOpacity ?? 0.9 : 0.9;
  const isDarkTheme = useIsDarkTheme();
  const mouseX = useMotionValue(-gradientSize);
  const mouseY = useMotionValue(-gradientSize);
  const orbX = useSpring(mouseX, {
    stiffness: 250,
    damping: 30,
    mass: 0.6
  });
  const orbY = useSpring(mouseY, {
    stiffness: 250,
    damping: 30,
    mass: 0.6
  });
  const orbVisible = useSpring(0, {
    stiffness: 300,
    damping: 35
  });
  const modeRef = useRef(mode);
  const glowOpacityRef = useRef(glowOpacity);
  const gradientSizeRef = useRef(gradientSize);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    glowOpacityRef.current = glowOpacity;
  }, [glowOpacity]);
  useEffect(() => {
    gradientSizeRef.current = gradientSize;
  }, [gradientSize]);
  const reset = useCallback((reason: ResetReason = "leave") => {
    const currentMode = modeRef.current;
    if (currentMode === "orb") {
      if (reason === "enter") orbVisible.set(glowOpacityRef.current);else orbVisible.set(0);
      return;
    }
    const off = -gradientSizeRef.current;
    mouseX.set(off);
    mouseY.set(off);
  }, [mouseX, mouseY, orbVisible]);
  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  }, [mouseX, mouseY]);
  useEffect(() => {
    reset("init");
  }, [reset]);

  // Use shared global listener instead of per-instance listeners
  useGlobalResetListener(() => reset("global"));
  return <motion.div className={cn("group relative isolate overflow-hidden rounded-[inherit] border border-transparent", className, stylexProps.className)} onPointerMove={handlePointerMove} onPointerLeave={() => reset("leave")} onPointerEnter={() => reset("enter")} style={{
    ...stylexProps.style,
    background: useMotionTemplate`
          linear-gradient(var(--color-background) 0 0) padding-box,
          radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px,
            ${gradientFrom},
            ${gradientTo},
            var(--color-border) 100%
          ) border-box
        `
  }}>
      {decoration}
      <div className="bg-background absolute inset-px z-20 rounded-[inherit]" />

      {mode === "gradient" && <motion.div suppressHydrationWarning className="pointer-events-none absolute inset-px z-30 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: useMotionTemplate`
              radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px,
                ${gradientColor},
                transparent 100%
              )
            `,
      opacity: gradientOpacity
    }} />}

      {mode === "orb" && <motion.div suppressHydrationWarning aria-hidden="true" className="pointer-events-none absolute z-30" style={{
      width: glowSize,
      height: glowSize,
      x: orbX,
      y: orbY,
      translateX: "-50%",
      translateY: "-50%",
      borderRadius: 9999,
      filter: `blur(${glowBlur}px)`,
      opacity: orbVisible,
      background: `linear-gradient(${glowAngle}deg, ${glowFrom}, ${glowTo})`,
      mixBlendMode: isDarkTheme ? "screen" : "multiply",
      willChange: "transform, opacity"
    }} />}
      <div className="relative z-40 w-full">{children}</div>
    </motion.div>;
}